import type { FC } from 'react';

import { Suspense } from 'react';
import { ErrorBoundary } from '@packages/shared';
import type { SxProps } from '@mui/system';
import type { Filter } from '../../api/rest/types';
import type { SelectedFilterValue } from '../../types';
import { useFilterGroupValues } from '../../hooks/useFilterGroupValues';
import { FilterDetailsLoader } from '../FilterDetailsLoader';
import { SimpleFilterValueGridList } from '../SimpleFilterValueGridList';
import { BasicFilterValueList } from '../BasicFilterValueList';
import { FilterRangeSlider } from '../FilterRangeSlider';

export type FilterDetailsProps = {
  /** The filter for which to display all selectable values */
  filter: Filter;
  /** Currently selected filter value ids */
  selectedFilterValues: SelectedFilterValue[];
  /** Callback fired when the filter selection changes */
  onChange?: (value: SelectedFilterValue) => void;
  /** Callback fired when an error occurs */
  onLoadingError: () => void;
  /** Additional styles which should be applied on the container */
  contentContainerStyle?: SxProps;
};

/**
 * Determines which control to show for adjusting the details for a specific filter
 * */
const FilterDetailsInternal: FC<Omit<FilterDetailsProps, 'onLoadingError'>> = ({
  filter,
  selectedFilterValues,
  onChange,
  contentContainerStyle,
}) => {
  const currentValue = selectedFilterValues?.find(
    (selectedValue) => selectedValue.filterId === filter.id,
  );

  const { response } = useFilterGroupValues(filter.id);

  if (filter.type !== response.type) {
    throw new Error('Filter type mismatch');
  }

  return (
    <>
      {response.type === 'patch' && (
        <SimpleFilterValueGridList
          contentContainerStyle={contentContainerStyle}
          groupedValues={response.groupedValues}
          selectedIds={currentValue?.selectedValues ?? []}
          onChange={(selectedValues) => onChange?.({ filterId: filter.id, selectedValues })}
        />
      )}
      {response.type === 'list' && (
        <BasicFilterValueList
          contentContainerStyle={contentContainerStyle}
          filterName={response.name}
          groupedValues={response.groupedValues}
          selectedIds={currentValue?.selectedValues ?? []}
          onChange={(selectedValues) => onChange?.({ filterId: filter.id, selectedValues })}
          isSearchable={response.isSearchable}
          isSingleSelection={response.isSingleSelection}
        />
      )}
      {/* re-check necessary because of typescript type narrowing */}
      {response.type === 'slider' && (
        <FilterRangeSlider
          contentContainerStyle={contentContainerStyle}
          filter={response}
          groupedValues={response.groupedValues}
          selectedRange={currentValue?.selectedRange}
          onChangeRange={(selectedRange) =>
            onChange?.({
              filterId: filter.id,
              selectedValues: currentValue?.selectedValues ?? [],
              selectedRange,
            })
          }
          selectedFilterValues={currentValue?.selectedValues}
          onChangeFilterValues={(selectedValues) =>
            onChange?.({
              filterId: filter.id,
              selectedValues,
              selectedRange: currentValue?.selectedRange,
            })
          }
        />
      )}
    </>
  );
};

/**
 * A ready to use wrapper with the matching loading state to display.
 * Besides the loading state it handles also the call after an error appears
 * */
export const FilterDetails: FC<FilterDetailsProps> = (props: FilterDetailsProps) => {
  const { filter, onLoadingError } = props;

  return (
    <ErrorBoundary errorCallback={onLoadingError}>
      <Suspense fallback={<FilterDetailsLoader filter={filter} />}>
        <FilterDetailsInternal {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

// default export necessary for next/dynamic
// eslint-disable-next-line import/no-default-export
export default FilterDetails;
